import { useEffect, useRef, useState } from "react";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread";
import MainCard from "../../../../components/MainCard";
import Transitions from "../../../../components/@extended/Transitions";

import BellOutlined from "@ant-design/icons/BellOutlined";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";
import { useGetNotificationsMutation, useGetNotificationsQuery, useReadAllNotificationMutation, useReadNotificationMutation } from "../../../../../features/common/CommonAPI";
import { useNavigate } from "react-router-dom";
import { auth, encryptLocalStorageData, getRootURL, notificationRedirect } from "../../../../../helper/Utility";

const avatarSX = {
  width: 36,
  height: 36,
  fontSize: "1rem",
};

const actionSX = {
  mt: "6px",
  ml: 1,
  top: "auto",
  right: "auto",
  alignSelf: "flex-start",
  transform: "none",
};

const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export default function Notification() {
  const [readNotification, { isLoading: isReadLoading, isError: isReadError }] = useReadNotificationMutation();
  const [readAllNotification, { isLoading: isReadAllLoading, isError: isReadAllError }] = useReadAllNotificationMutation();
  const page = 1
  const perPage = 5
  const { data: notifications, isLoading, isError } = useGetNotificationsQuery({ page, perPage });

  useEffect(() => {
    const oldAuth = auth("admin")
    if (oldAuth?.user_role != "admin") {
      const user = notifications?.user
      if(!isLoading){
        const updatedData = {
          ...oldAuth,
          business_name: user?.businessName,
          fullname: user?.firstName + ' ' + user?.lastName,
          email: user?.email,
          user_role: user?.role,
          status: user?.status,
          isDeleted: user?.isDeleted,
        };
        encryptLocalStorageData('admin-secret', updatedData, 'DoNotTryToAccess');
      }
    }
  }, [notifications]);


  const navigate = useNavigate()
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  
  const handleToggle = () => {
    if(notifications?.data?.length > 0){
      setOpen((prevOpen) => !prevOpen)
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleCall = async (action) => {
    if (action === "read_all") {
      await readAllNotification().unwrap();
      setOpen(false);
    } else {
      navigate(`/${(getRootURL())}/notifications`)
      setOpen(false);
    }
  };

  const handleNotificationClick = async (row) => {
    notificationRedirect(navigate, row)
    if (!row?.isRead) {
      await readNotification({ id: row?._id }).unwrap();
    }
  };

  const iconBackColorOpen = "grey.100";
  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{
          color: "text.primary",
          bgcolor: open ? iconBackColorOpen : "transparent",
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className="bell_icon"
      >
        <Badge badgeContent={notifications?.totalUnreadResults} className="badge-color">
          <i class="fa-solid fa-bell"></i>
        </Badge>
      </IconButton>
      <Popper
        className="notify_pop"
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            { name: "offset", options: { offset: [matchesXs ? -5 : 0, 9] } },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={matchesXs ? "top" : "top-right"}
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: "100%",
                minWidth: 285,
                maxWidth: { xs: 285, md: 420 },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <Tooltip title="Mark all as read">
                      <IconButton
                        color="success"
                        size="small"
                        onClick={() => handleCall("read_all")}
                      >
                        <CheckCircleOutlined style={{ fontSize: "1.15rem" }} />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      "& .MuiListItemButton-root": {
                        py: 0.5,
                        "&.Mui-selected": {
                          bgcolor: "grey.50",
                          color: "text.primary",
                        },
                        "& .MuiAvatar-root": avatarSX,
                        "& .MuiListItemSecondaryAction-root": {
                          ...actionSX,
                          position: "relative",
                        },
                      },
                    }}
                  >
                    {
                        notifications?.data?.map((item) => (
                          <ListItemButton key={item.id} onClick={() => { handleNotificationClick(item); setOpen(false); }} 
                          style={{ cursor: "pointer", backgroundColor: !item.isRead ? "#daefff" : "", }}>
                            <ListItemAvatar>
                              <Avatar sx={{ width: 28, height: 28, fontSize: "1rem", color: "#1e90ff", bgcolor: "#fff", }} >
                                <MarkChatUnreadIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                              primary={<Typography variant="h6" sx={{ color: "#000" }}> {item.message} </Typography>}
                              secondary={timeAgo(item.createdAt)}
                            />
                          </ListItemButton>
                        ))
                       
                    }
                    <ListItemButton sx={{ textAlign: "center", py: `${12}px !important` }} onClick={() => handleCall("view")}>
                      <ListItemText
                        primary={<Typography variant="h6" color="primary">View All</Typography>}
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
